import { useState, useRef, useEffect, useContext } from 'react'
import { Button } from '../../Button'
import { GlassHoverEffect } from '../../GlassHoverEffect'
import {
  HiChevronDown,
  HiArrowRightOnRectangle,
  HiOutlineUser,
  HiOutlineEnvelope,
} from 'react-icons/hi2'
import { AuthContext } from '../../../contexts'
import { notificacaoService } from '../../../services'
import { toast } from '../../Toast'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '../../../utils/formatter'

interface MenuItem {
  id: string
  label: string
  icon: React.ReactNode
  onClick: () => void
  variant?: 'default' | 'danger'
}

const UserMenu = () => {
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [isTogglingEmails, setIsTogglingEmails] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Proteção contra uso fora do AuthProvider
  const auth = useContext(AuthContext)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showUserMenu])

  if (!auth) {
    return null
  }

  const { user, logout, refreshUser } = auth;

  const handleLogout = async () => {
    await logout()
  }

  const handleToggleEmails = async () => {
    if (isTogglingEmails) return

    setIsTogglingEmails(true)
    try {
      const response = await notificacaoService.toggleReceberEmails()
      
      // Atualiza o usuário buscando dados atualizados
      await refreshUser()
      
      toast.success(
        response.emails 
          ? 'Você passará a receber emails de notificação' 
          : 'Você não receberá mais emails de notificação'
      )
    } catch {
      toast.error('Não foi possível alterar a preferência de emails')
    } finally {
      setIsTogglingEmails(false)
    }
  }

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  // Menu items array for GlassHoverEffect
  const menuItems: MenuItem[] = [
    {
      id: 'profile',
      label: 'Meu Perfil',
      icon: <HiOutlineUser className='w-4 h-4' />,
      onClick: () => {
        // TODO: Navigate to profile page
        setShowUserMenu(false)
      },
    },
    {
      id: 'logout',
      label: 'Sair da conta',
      icon: <HiArrowRightOnRectangle className='w-4 h-4' />,
      onClick: handleLogout,
      variant: 'danger' as const,
    },
  ]

  // Get active index for GlassHoverEffect (default to first item)
  const getActiveIndex = () => 0

  return (
    <div className='relative' ref={menuRef}>
      <Button
        variant='ghost'
        size='sm'
        onClick={() => setShowUserMenu(!showUserMenu)}
        className='flex items-center gap-1 sm:gap-3 px-2 sm:px-3 py-2 h-9 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors'
      >
        <div className='h-8 w-8 sm:h-7 sm:w-7 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0'>
          <span className='text-white text-xs font-bold'>
            {user?.name ? getUserInitials(user.name) : 'U'}
          </span>
        </div>
        {/* Texto oculto em mobile */}
        <div className='hidden sm:block text-left'>
          <p className='text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-[100px] md:max-w-[120px]'>
            {user?.name || 'Usuário'}
          </p>
          <p className='text-xs text-gray-500 dark:text-gray-400'>
            {user?.funcao_usuario || 'Funcionário'}
          </p>
        </div>
        <HiChevronDown
          className={cn(
            'w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 hidden sm:block',
            showUserMenu && 'rotate-180'
          )}
        />
      </Button>

      <AnimatePresence>
        {showUserMenu && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              "absolute top-full mt-2 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl shadow-2xl z-[9999] overflow-hidden",
              // Mobile: centralizado na tela
              "fixed left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)]",
              // Desktop: posicionado à direita do botão
              "sm:fixed sm:left-auto sm:translate-x-0 sm:right-4 sm:w-64",
              "md:absolute md:right-0 md:w-64"
            )}          >
            {/* User Info Header */}
            <div className='px-4 py-3 border-b border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-700'>
              <div className='flex items-center gap-3'>
                <div className='h-10 w-10 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center shadow-sm flex-shrink-0'>
                  <span className='text-white text-sm font-bold'>
                    {user?.name ? getUserInitials(user.name) : 'U'}
                  </span>
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='font-medium text-gray-900 dark:text-gray-100 text-sm truncate'>
                    {user?.name || 'Usuário'}
                  </p>
                  <p className='text-gray-500 dark:text-gray-400 text-xs truncate'>
                    {user?.funcao_usuario || 'Funcionário'}
                  </p>
                </div>
              </div>
            </div>

            {/* Email Notifications Toggle */}
            <div className='px-4 py-3 border-b border-gray-200 dark:border-neutral-700'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-3'>
                  <HiOutlineEnvelope className='w-5 h-5 text-gray-500 dark:text-gray-400' />
                  <div>
                    <p className='text-sm font-medium text-gray-700 dark:text-gray-300'>
                      Receber emails
                    </p>
                    <p className='text-xs text-gray-500 dark:text-gray-400'>
                      Notificações por email
                    </p>
                  </div>
                </div>
                <button
                  type='button'
                  onClick={handleToggleEmails}
                  disabled={isTogglingEmails}
                  className={cn(
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
                    user?.emails ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-700'
                  )}
                  aria-label='Toggle email notifications'
                >
                  <span
                    className={cn(
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      user?.emails ? 'translate-x-6' : 'translate-x-1'
                    )}
                  />
                </button>
              </div>
            </div>

            {/* Menu Items with GlassHoverEffect */}
            <div className='p-2'>
              <GlassHoverEffect
                activeIndex={getActiveIndex()}
                direction='vertical'
                className='flex flex-col gap-1 relative'
                glassClassName='rounded-lg shadow-sm bg-blue-50/90 dark:bg-blue-900/20 border border-blue-200/60 dark:border-blue-700/40 backdrop-blur-sm'
              >
                {({ containerRef, containerProps, getItemProps }) => (
                  <div
                    ref={containerRef as React.RefObject<HTMLDivElement>}
                    className='flex flex-col gap-1 relative'
                    {...containerProps}
                  >
                    {menuItems.map((item, index) => (
                      <button
                        key={item.id}
                        onClick={item.onClick}
                        {...getItemProps(index)}
                        className={cn(
                          'flex items-center w-full px-3 py-2.5 text-sm hover:bg-transparent transition-colors rounded-lg relative z-10',
                          item.variant === 'danger'
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-700 dark:text-gray-300'
                        )}
                      >
                        <span
                          className={cn(
                            'mr-3',
                            item.variant === 'danger'
                              ? 'text-red-500 dark:text-red-400'
                              : 'text-gray-500 dark:text-gray-400'
                          )}
                        >
                          {item.icon}
                        </span>
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </GlassHoverEffect>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export { UserMenu }
